import { API_DELETE } from '@cadstrom/backend/shared';
import SaveIcon from '@mui/icons-material/Save';
import Button from '@mui/material/Button';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { ClearParametersDialog } from '#screens/sigma-model-parameter-form/clear-parameters-dialog.js';
import { useModelTypeChangeGuard } from '#screens/sigma-model-parameter-form/use-model-type-change-guard.js';
import { SigmaModelTypePicker } from '#src/components/sigma-model-type-picker/sigma-model-type-picker.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#src/utils/use-auto-save/use-auto-save.js';
import { PackageReviewsSection } from './package-reviews-section.js';
import { PackageTagsSection } from './package-tags-section.js';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/general/',
)({
	staticData: {
		navigation: {
			label: 'Package: General',
			keywords: ['general', 'package', 'sigma', 'model', 'type'],
		},
	},
	beforeLoad: ({ params: { packageId }, context: { trpc } }) => ({
		getTagsForPackageOptions: trpc.tag.getMany.queryOptions({ packageId }),
		getSigmaModelDefinitionsOptions: trpc.misc.getSigmaModelDefinitions.queryOptions({}, { staleTime: Infinity }),
	}),
	loader: ({
		context: { queryClient, getPackageOptions, getTagsForPackageOptions, getSigmaModelDefinitionsOptions },
	}) => {
		void queryClient.prefetchQuery(getPackageOptions);
		void queryClient.prefetchQuery(getTagsForPackageOptions);
		void queryClient.prefetchQuery(getSigmaModelDefinitionsOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') + 'General • Package Editor • Cadstrom',
			},
		],
	}),
	component: GeneralTab,
});

function GeneralTab() {
	const queryClient = useQueryClient();
	const trpc = useTRPC();

	const { getPackageOptions, getTagsForPackageOptions, getSigmaModelDefinitionsOptions } = Route.useRouteContext();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);
	const { data: tagsData } = useSuspenseQuery(getTagsForPackageOptions);
	const {
		data: { sigmaModelDefinitions },
	} = useSuspenseQuery(getSigmaModelDefinitionsOptions);

	const { mutate: removeTag, isPending: isRemovingTag } = useMutation(
		trpc.tag.removeFromPackage.mutationOptions({
			onSuccess: () => {
				void queryClient.invalidateQueries({
					queryKey: getTagsForPackageOptions.queryKey,
				});
			},
		}),
	);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res, variables) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				// The cleared names are an instruction for the save, not a field the server echoes back, so
				// the draft has to drop the ones just sent or it would stay permanently dirty against a
				// server state that resets them — keeping Save armed and re-issuing the same deletions on
				// every later autosave. Names added while this save was in flight stay queued.
				const savedParameters = variables.data.sigmaModelParameters;
				const sent = new Set(
					savedParameters !== undefined && 'update' in savedParameters ? Object.keys(savedParameters.update ?? {}) : [],
				);
				setDraftPackage((prev) => ({
					...prev,
					clearedParameterNames: prev.clearedParameterNames.filter((name) => !sent.has(name)),
				}));

				setServerPackage({
					sigmaModelType: res.pkg.sigmaModelType,
					clearedParameterNames: [],
				});
			},
		}),
	);

	const {
		draftState: draftPackage,
		setDraftState: setDraftPackage,
		setServerState: setServerPackage,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: {
			sigmaModelType: pkg.sigmaModelType,
			clearedParameterNames: [] as string[],
		},
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftPackage) =>
			await updatePackage({
				id: pkg.id,
				data: {
					sigmaModelType: draftPackage.sigmaModelType,
					...(draftPackage.clearedParameterNames.length > 0 && {
						sigmaModelParameters: {
							update: Object.fromEntries(draftPackage.clearedParameterNames.map((name) => [name, API_DELETE])),
						},
					}),
				},
			}),
	});

	const applyModelTypeChange = useCallback(
		(modelType: string, clearedParameterNames: string[]) => {
			setDraftPackage((prev) => ({ ...prev, sigmaModelType: modelType, clearedParameterNames }));
		},
		[setDraftPackage],
	);

	const storedSigmaModelDefinition = sigmaModelDefinitions.find((model) => model.name === pkg.sigmaModelType);

	const { requestModelTypeChange, pendingChange, confirmPendingChange, cancelPendingChange } = useModelTypeChangeGuard({
		parameters: pkg.sigmaModelParameters,
		sigmaModelDefinitions,
		onApply: applyModelTypeChange,
	});

	const formLabel = (text: string, htmlFor: string) => (
		<label className='text-right text-gray-11' htmlFor={htmlFor}>
			{text}
		</label>
	);

	return (
		<div className='grow overflow-auto px-3 py-2' ref={saveAreaContainerRef}>
			<Button
				disabled={!isDirty || !draftPackage.sigmaModelType}
				loading={updatePackageIsPending}
				startIcon={<SaveIcon />}
				className='float-right mb-2 ml-3'
				onClick={() => void handleSave()}
			>
				<p>Save</p>
			</Button>

			<div className='grid w-full max-w-2xl grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2'>
				{formLabel('Sigma Model Type', 'sigma-model-type')}
				<SigmaModelTypePicker
					id='sigma-model-type'
					enableClear={false}
					modelType={draftPackage.sigmaModelType}
					onModelTypeChange={(modelType) => {
						if (!modelType) {
							return;
						}
						requestModelTypeChange(modelType);
					}}
				/>
			</div>

			<div className='mt-6 max-w-2xl border-t border-gray-a4 pt-4'>
				<PackageTagsSection
					tags={tagsData.tags}
					onRemoveTag={(tagId) => removeTag({ packageId: pkg.id, tagId })}
					isRemoving={isRemovingTag}
				/>
			</div>

			<div className='mt-6 max-w-2xl border-t border-gray-a4 pt-4'>
				<PackageReviewsSection packageId={pkg.id} />
			</div>

			{pendingChange !== null && (
				<ClearParametersDialog
					open
					targetModelType={pendingChange.modelType}
					sigmaModelDefinition={storedSigmaModelDefinition}
					parameters={pendingChange.losses}
					onConfirm={confirmPendingChange}
					onCancel={cancelPendingChange}
				/>
			)}
		</div>
	);
}
